import { Link, useLocation } from "react-router-dom";
import {
  MEETING_JOIN_LEAD_MS,
  doctorHasNotOpenedStartedMeeting,
  nextClinicianMeeting,
  type ClinicianMeeting,
} from "@care-by-ella/contracts";
import { useViewPath } from "@/components/SupportView";
import {
  clockTime,
  countdownText,
  meetingKindLabel,
} from "@/lib/meeting-calendar";
import { patientMeetingHref } from "@/lib/patient-links";
import { useNow } from "@/lib/use-now";
import { MeetingSignals } from "./MeetingSignals";

// TES-1233 — Emelie: "Adminpåminnelse före mötet". The portal half of it
// (the email is MeetingReminderJob): on every page, from the moment the room
// opens — the same lead as the email and the patient's push — until the
// doctor has joined, a banner names the meeting and links straight into it.
// After the start it carries the "has started but not opened" warning.
//
// Not shown on the meeting itself: the doctor is already where it points.
// Nor on Idag, whose "Nästa möte" card says the same thing.
export function UpcomingMeetingBanner({
  meetings,
}: {
  meetings: ClinicianMeeting[] | null;
}) {
  const now = useNow();
  const viewPath = useViewPath();
  const location = useLocation();
  const next = nextClinicianMeeting(meetings, now);
  if (!next || next.doctorJoinedAt) return null;
  const startsIn = new Date(next.scheduledAt).getTime() - now;
  const due =
    startsIn <= MEETING_JOIN_LEAD_MS ||
    doctorHasNotOpenedStartedMeeting(next, now);
  if (!due) return null;
  const href = viewPath(patientMeetingHref(next.patient.id, next.id));
  const [hrefPath, hrefSearch = ""] = href.split("?");
  const onIt =
    location.pathname === hrefPath &&
    new URLSearchParams(location.search).toString() ===
      new URLSearchParams(hrefSearch).toString();
  // Idag carries the same meeting in its "Nästa möte" card.
  if (onIt || location.pathname.endsWith("/today")) return null;
  const name =
    next.patient.name?.trim() || `…${next.patient.personalNumberLast6}`;

  return (
    <div
      role="status"
      className="mb-md flex flex-col gap-sm rounded-md border-2 border-brand bg-surface p-md sm:flex-row sm:items-center sm:justify-between"
    >
      <div className="flex min-w-0 flex-col gap-xs">
        <p className="text-sm">
          <span className="font-semibold">Nästa möte</span>{" "}
          {clockTime(next.scheduledAt)} · {name} · {meetingKindLabel(next)}
        </p>
        <p className="text-sm text-textSecondary">{countdownText(next, now)}</p>
        <MeetingSignals meeting={next} now={now} />
      </div>
      <Link
        to={href}
        className="shrink-0 self-start rounded-md bg-brand px-md py-sm text-sm font-medium text-textOnBrand hover:opacity-90 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2 sm:self-center"
      >
        Öppna mötet
      </Link>
    </div>
  );
}
